import { env } from "./config/env";

const azureClientId = env('REACT_APP_AZURE_CLIENT_ID');
const azureB2cApiClientId = env('REACT_APP_AZURE_B2C_API_CLIENT_ID');
const azureB2cDomain = env('REACT_APP_AZURE_B2C_DOMAIN');
const azureB2cAuthorityDomain = env('REACT_APP_AZURE_B2C_AUTHORITY_DOMAIN');
const azureB2cUserFlowSignIn = env('REACT_APP_AZURE_B2C_USER_FLOW_SIGNIN');
const azureB2cUserFlowForgotPassword = env('REACT_APP_AZURE_B2C_USER_FLOW_FORGOT_PASSWORD');
const pharoBackendApiUrl = env('REACT_APP_PHARO_BACKEND_API_URL');

const isAuthBypass = env('REACT_APP_AUTH_BYPASS') === 'true';

// Only import @azure/msal-browser when NOT in auth bypass mode.
// Importing the module triggers MSAL's internal initialization which
// attempts to contact Azure B2C — that fails in docker dev mode.
let LogLevel;
if (!isAuthBypass) {
    LogLevel = require("@azure/msal-browser").LogLevel;
}

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signIn: azureB2cUserFlowSignIn,
        forgotPassword: azureB2cUserFlowForgotPassword,
    },
    authorities: {
        signIn: {
            authority: `https://${azureB2cAuthorityDomain}/${azureB2cDomain}/${azureB2cUserFlowSignIn}`,
        },
        forgotPassword: {
            authority: `https://${azureB2cAuthorityDomain}/${azureB2cDomain}/${azureB2cUserFlowForgotPassword}`,
        },
    },
    authorityDomain: azureB2cAuthorityDomain
}


/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
        clientId: azureClientId, // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signIn.authority,
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: "/", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: isAuthBypass ? {} : {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        console.warn(message);
                        return;
                }
            }
        }
    }
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    api: {
        scopes: [
            `https://${azureB2cDomain}/${azureB2cApiClientId}/Api.Access`,
        ]
    },
    apiUsers: {
        endpoint: `${pharoBackendApiUrl}/users`,
    },
    apiUserArchive: {
        endpoint: `${pharoBackendApiUrl}/user-management/archive`,
    },
    apiUserUnarchive: {
        endpoint: `${pharoBackendApiUrl}/user-management/unarchive`,
    },
    apiUserInvite: {
        endpoint: `${pharoBackendApiUrl}/user-management/invite`,
    },
    apiCompany: {
        endpoint: `${pharoBackendApiUrl}/company`,
    },
    apiContent: {
        endpoint: `${pharoBackendApiUrl}/content`,
    },
    apiReport: {
        endpoint: `${pharoBackendApiUrl}/reports`,
    },
    apiMemberAnalytics: {
        endpoint: `${pharoBackendApiUrl}/member-analytics`,
    },
    apiQualityManagement: {
        endpoint: `${pharoBackendApiUrl}/quality-management`,
    },
    apiEacParticipationResponses: {
        endpoint: `${pharoBackendApiUrl}/eacParticipationResponses`,
    },
    apiCohortManagement: {
        endpoint: `${pharoBackendApiUrl}/cohort-management`,
    },
    apiAdvisoryGroup: {
        endpoint: `${pharoBackendApiUrl}/advisory-group`,
    },
    apiNotification: {
        endpoint: `${pharoBackendApiUrl}/notifications`,
    },
    apiLogClientError: {
        endpoint: `${pharoBackendApiUrl}/log-client-error`,
    },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.api.scopes]
};
